import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { taxiStatusColor } from "@/constants/status";
import { Notification } from "@/interface/entityInterface";
import { format } from "date-fns";
import { Car, Clock, AlertCircle, CheckCircle } from "lucide-react";
import { Dispatch, FC, SetStateAction } from "react";

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  notification: Notification | null;
}

const NotificationsDialog: FC<Props> = ({ open, setOpen, notification }) => {
  if (!notification) return;

  const parsedData = typeof notification.data == "string" ? JSON.parse(notification.data) : notification.data;
  const status = parsedData?.status || parsedData?.response?.status || "REJECTED"
  const statusColor = taxiStatusColor[status]
  const StatusIcon =
    parsedData?.response?.status === "ACCEPTED" ? CheckCircle : AlertCircle;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="my-auto overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{notification.title}</DialogTitle>
          <DialogDescription>{notification.message}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-full ${statusColor.dot}`}>
                <StatusIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">{notification.title}</h2>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(notification.createdAt), "PPpp")}
                </p>
              </div>
            </div>
            <Badge variant={notification.seen ? "secondary" : "default"}>
              {notification.seen ? "Read" : "Unread"}
            </Badge>
          </div>

          <Separator />

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Car className="h-5 w-5" />
                <span>Driver Response</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Status
                  </p>
                  <Badge className={statusColor.badge}>
                    {parsedData?.status || parsedData?.response?.status || "REJECTED"}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Order ID
                  </p>
                  <p className="font-mono text-sm">{parsedData?.taxiOrderId}</p>
                </div>
              </div>

              {parsedData?.response?.reason && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Reason
                  </p>
                  <p className="text-sm">{parsedData?.response?.reason}</p>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {parsedData?.response?.scheduled ? "Scheduled" : "Immediate"}
                </span>
              </div>
            </CardContent>
          </Card>

          {notification.user.drivers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Associated Drivers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {notification.user.drivers.map((driver) => (
                    <div
                      key={driver.id}
                      className="flex items-center space-x-2"
                    >
                      <Badge variant="outline">Driver {driver.name}</Badge>
                      <span className="text-sm text-muted-foreground">
                        ID: {driver.id}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Technical Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium text-muted-foreground">Type</p>
                  <p>{notification.type}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">Delivered</p>
                  <Badge
                    variant={notification.delivered ? "default" : "secondary"}
                  >
                    {notification.delivered ? "Yes" : "No"}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationsDialog;
